/**
 * Reusable Export/Import section for module settings pages.
 * Provides file export (download), file validation, and import with
 * a clear spinner + toast UX. Modules customize via i18n namespace
 * and entity count formatter.
 */

import { ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Upload, Loader2, FileCheck, AlertCircle, Tag } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Separator } from '../../../components/ui/separator';
import { ConfirmDialog } from './ConfirmDialog';
import { TagInput } from '../tags';
import type { TagSummary } from '@/generated/api/models/tagSummary';
import { useNotification } from '../../context/NotificationContext';
import { getGenericErrorMessage } from '../../utils/errors';
import {
  useModuleExport,
  useModuleValidateImport,
  useModuleImport,
  type ExportImportValidateResult,
  type ExportParams,
} from '../../hooks/useExportImport';
import { toDateOnlyString } from '@/shared/utils/dateFormat';

interface ExportImportSectionProps {
  /** Module API route name (e.g. 'tasks', 'sales', 'contacts') */
  moduleName: string;
  /** i18n namespace for this module (e.g. 'tasks', 'sales') */
  i18nNamespace: string;
  /** React Query cache keys to invalidate after import (e.g. ['workspaces', 'boards']) */
  invalidateKeys?: string[][];
  /** Render custom entity count summary from validation metadata */
  formatEntityCounts?: (counts: Record<string, number>) => string;
  /** Optional extra content before the export button (e.g. selective export dialog trigger) */
  exportAction?: (props: {
    onExport: (params?: ExportParams) => Promise<void>;
    isPending: boolean;
  }) => ReactNode;
  /** Optional extra sections after import (e.g. third-party import links) */
  children?: ReactNode;
  /**
   * When set, shows an optional tag picker; the chosen tags are attached to
   * every imported taggable entity. The value is the primary taggable entity
   * type (`{module}.{entity}`) used for autocomplete personalization, e.g.
   * `master_data.contact`.
   */
  tagEntityType?: string;
}

export function ExportImportSection({
  moduleName,
  i18nNamespace,
  invalidateKeys,
  formatEntityCounts,
  exportAction,
  children,
  tagEntityType,
}: ExportImportSectionProps) {
  const { t } = useTranslation([i18nNamespace, 'common']);
  const { showSuccess } = useNotification();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ExportImportValidateResult | null>(null);
  const [importConfirmOpen, setImportConfirmOpen] = useState(false);
  const [importConfirmChecked, setImportConfirmChecked] = useState(false);
  const [importTags, setImportTags] = useState<TagSummary[]>([]);

  const exportMutation = useModuleExport(moduleName);
  const validateMutation = useModuleValidateImport(moduleName);
  const importMutation = useModuleImport(moduleName);

  const handleExport = useCallback(async (params?: ExportParams) => {
    try {
      const blob = await exportMutation.mutateAsync(params);

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${moduleName}_export_${toDateOnlyString(new Date())}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccess(t(`${i18nNamespace}:exportImport.exportSuccess`));
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  }, [exportMutation, moduleName, i18nNamespace, showSuccess, t]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setValidationResult(null);

    try {
      const result = await validateMutation.mutateAsync(file);
      setValidationResult(result);
    } catch (error) {
      // Global mutation-error handler already toasts the specific reason; this
      // inline panel shows the same unified generic message (with a reference)
      // so the user has a report-able reference next to the failed file.
      setValidationResult({
        valid: false,
        errors: [getGenericErrorMessage(error)],
        metadata: null,
        exported_at: null,
        source_tenant_id: null,
      });
    }
  }, [validateMutation]);

  const handleImport = useCallback(async () => {
    if (!selectedFile || !validationResult?.valid) return;

    try {
      const result = await importMutation.mutateAsync({
        file: selectedFile,
        tagIds: importTags.map((tag) => tag.id),
      });

      // Clear file state
      setSelectedFile(null);
      setValidationResult(null);
      setImportTags([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Invalidate relevant caches so UI reflects imported data
      if (invalidateKeys) {
        for (const key of invalidateKeys) {
          await queryClient.invalidateQueries({ queryKey: key });
        }
      }

      showSuccess(
        t(`${i18nNamespace}:exportImport.importSuccess`, {
          count: result.total_imported,
        })
      );
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  }, [
    selectedFile, validationResult, importTags, importMutation, invalidateKeys,
    queryClient, i18nNamespace, showSuccess, t,
  ]);

  const handleClearFile = useCallback(() => {
    setSelectedFile(null);
    setValidationResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const entityCountSummary = validationResult?.valid && validationResult.metadata?.entity_counts
    ? formatEntityCounts?.(validationResult.metadata.entity_counts)
    : null;

  return (
    <div className="space-y-6">
      <Separator />

      <div>
        <h3 className="text-base font-medium mb-1">
          {t(`${i18nNamespace}:exportImport.title`)}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t(`${i18nNamespace}:exportImport.description`)}
        </p>
      </div>

      {/* Export Section */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium">
          {t(`${i18nNamespace}:exportImport.exportTitle`)}
        </h4>
        <p className="text-xs text-muted-foreground">
          {t(`${i18nNamespace}:exportImport.exportDescription`)}
        </p>
        {exportAction ? (
          exportAction({ onExport: handleExport, isPending: exportMutation.isPending })
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleExport()}
            disabled={exportMutation.isPending}
          >
            {exportMutation.isPending ? (
              <Loader2 className="h-4 w-4 me-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 me-2" />
            )}
            {t(`${i18nNamespace}:exportImport.exportButton`)}
          </Button>
        )}
      </div>

      {/* Import Section */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium">
          {t(`${i18nNamespace}:exportImport.importTitle`)}
        </h4>
        <p className="text-xs text-muted-foreground">
          {t(`${i18nNamespace}:exportImport.importDescription`)}
        </p>

        <div className="flex items-center gap-2">
          <Input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            className="max-w-xs text-sm"
            disabled={validateMutation.isPending || importMutation.isPending}
          />
          {selectedFile && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearFile}
              disabled={importMutation.isPending}
            >
              {t('common:app.clear')}
            </Button>
          )}
        </div>

        {validateMutation.isPending && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t(`${i18nNamespace}:exportImport.validating`)}
          </div>
        )}

        {validationResult && (
          <Alert variant={validationResult.valid ? 'default' : 'destructive'}>
            {validationResult.valid ? (
              <FileCheck className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertDescription>
              {validationResult.valid ? (
                <div>
                  <p>{t(`${i18nNamespace}:exportImport.validFile`)}</p>
                  {entityCountSummary && (
                    <p className="text-xs mt-1">{entityCountSummary}</p>
                  )}
                </div>
              ) : (
                <div>
                  <p>{t(`${i18nNamespace}:exportImport.invalidFile`)}</p>
                  {validationResult.errors && (
                    <ul className="text-xs mt-1 list-disc list-inside">
                      {validationResult.errors.map((error, i) => (
                        <li key={i}>{error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        {tagEntityType && validationResult?.valid && !importMutation.isPending && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Tag className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">
                {t('common:exportImport.tagImported')}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('common:exportImport.tagImportedHint')}
            </p>
            <TagInput
              entityType={tagEntityType}
              value={importTags}
              onChange={setImportTags}
              autoSave={false}
            />
          </div>
        )}

        {importMutation.isPending ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t(`${i18nNamespace}:exportImport.importing`)}
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => { setImportConfirmChecked(false); setImportConfirmOpen(true); }}
            disabled={!validationResult?.valid || importMutation.isPending}
          >
            <Upload className="h-4 w-4 me-2" />
            {t(`${i18nNamespace}:exportImport.importButton`)}
          </Button>
        )}
      </div>

      {children}

      {/* Import confirmation dialog */}
      <ConfirmDialog
        open={importConfirmOpen}
        onClose={() => setImportConfirmOpen(false)}
        onConfirm={() => {
          if (!importConfirmChecked) return;
          setImportConfirmOpen(false);
          handleImport();
        }}
        title={t('common:exportImport.confirmTitle')}
        message={t('common:exportImport.confirmMessage')}
        confirmLabel={t('common:exportImport.confirmImport')}
        cancelLabel={t('common:app.cancel')}
        isLoading={!importConfirmChecked}
        destructive
      >
        <div className="flex items-start gap-2 py-2">
          <Checkbox
            id="import-confirm-checkbox"
            checked={importConfirmChecked}
            onCheckedChange={(checked) => setImportConfirmChecked(checked === true)}
          />
          <Label htmlFor="import-confirm-checkbox" className="text-sm leading-tight cursor-pointer">
            {t('common:exportImport.confirmCheckbox')}
          </Label>
        </div>
      </ConfirmDialog>
    </div>
  );
}
